//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  DISTRIBUTION_ENDPOINTS,
  IContentLibraryItem,
} from '@apis/content-library/contentLibraryApiModel';
import { RIGHTS_STATUS } from '@apis/rights-management/rightsManagementApiModels';
import Media from '@components/atoms/media/Media';
import { getContentLibraryItemMediaType } from '@components/atoms/media/getMediaType';
import MediaCard from '@components/molecules/media-card/MediaCard';
import { useCaptionStatus } from '@hooks/useCaptionStatus';
import { useBoundActionCreators } from '@hooks/useCuralateStore';
import Cuid from '@models/Cuid';
import PartialCuid from '@models/PartialCuid';
import {
  getContentItemDetailsService,
  getUniversalImageActionsView,
} from '@react-bridge/wrappers/ng-service-provider';
import { getClientId } from '@redux/selectors/getClientId';
import { actions as streamDrawerActions } from '@streams/actions/streamDrawerActions';
import { openUiaToView } from '@streams-drawer/streams-drawer-content/streams-drawer-moderation-content/streamsDrawerModerationUtils';

import { CAPTIONS_STATUS_VALUE } from 'app/constants/mute-tooltip-content';

interface Props {
  item: IContentLibraryItem;
  disableBulkActions: boolean;
  width: number;
}

const StreamsDrawerCarouselCard = ({
  disableBulkActions,
  item,
  width,
}: Props) => {
  const { updateItemWithUiaUpdates } =
    useBoundActionCreators<typeof streamDrawerActions>(streamDrawerActions);

  const UniversalImageActionsView = getUniversalImageActionsView();

  const [productTagsCount, setProductTagsCount] = useState(0);
  const [distributionEndpoints, setDistributionEndpoints] = useState<
    DISTRIBUTION_ENDPOINTS[]
  >([]);
  const [rightsStatus, setRightsStatus] = useState<undefined | RIGHTS_STATUS>();
  const [muteStatus, setMuteStatus] = useState<boolean | undefined>();
  const [captionStatus, setCaptionStatus] = useState<boolean | undefined>();
  const clientId = useSelector(getClientId);

  const cuid = Cuid.build({ itemType: item.itemType, id: item.id }, clientId);
  const captionSelectedItems = useSelector(
    (state) => state.bulkActions.selectedItemsForCaptions,
  );
  const captionTurnOnOffState = useSelector(
    (state) => state.bulkActions.captionTurnOnOffState,
  );

  const captionStatusValue: string | undefined = captionSelectedItems[cuid];

  useCaptionStatus(captionTurnOnOffState, captionStatusValue, setCaptionStatus);

  const isCaptionInProgress =
    captionTurnOnOffState === 'on' &&
    captionStatusValue === CAPTIONS_STATUS_VALUE.IN_PROGRESS;

  // Whenever the item changes, we'll need to hydrate it with some
  // additional metadata. This hook fetches item details and stores
  // them as state.
  useEffect(() => {
    if (item) {
      const contentLibraryItemDetailsService = getContentItemDetailsService();
      const partialCuid = PartialCuid.build(item);
      const itemDetailsStream =
        contentLibraryItemDetailsService.requestItemDetails(partialCuid, false);

      itemDetailsStream.subscribe((itemDetailsMap) => {
        const itemDetails = itemDetailsMap?.[partialCuid];

        if (itemDetails) {
          setProductTagsCount(itemDetails.productTagsCount);
          setDistributionEndpoints(itemDetails.distributionEndpoints);
          setRightsStatus(itemDetails.rightsStatus);
          setMuteStatus(itemDetails.muteStatus);
          setCaptionStatus(itemDetails.captionStatus);
        }
      });
    }
  }, [item]);

  return (
    <StreamMediaCard
      id={item.id}
      image={
        <StreamMedia
          bulkSelectionLayer={{ enabled: !disableBulkActions }}
          id={item.id}
          imageUrl={item.imageUrl}
          itemType={item.itemType}
          metadataLayer={{
            isCaptionInProgress: isCaptionInProgress,
            enabled: true,
            mediaType: getContentLibraryItemMediaType(item),
            itemDetails: {
              productTagsCount,
              distributionEndpoints,
              rightsStatus,
              muteStatus,
              captionStatus,
            },
          }}
          onItemClick={(e) => {
            openUiaToView(
              e,
              UniversalImageActionsView.Details.name,
              item.itemType,
              item.id,
              updateItemWithUiaUpdates,
            );
          }}
          width={width}
        />
      }
      itemType={item.itemType}
      width={width}
    />
  );
};

const StreamMediaCard = styled(MediaCard)<{ width: number }>(({ width }) => {
  return `
    width: ${width}px;
    height: ${width}px;
  `;
});

const StreamMedia = styled(Media)<{ width: number }>(({ width }) => {
  return `
    width: ${width}px;
    height: ${width}px;
  `;
});

export default StreamsDrawerCarouselCard;
